// Modal opened from the pause-menu "Share" button. Shows the share URL and
// gives the user three ways to send it: native Web Share (when available),
// copy-to-clipboard, and a selectable text input as the last-resort fallback.
// DOM-only — the scene supplies the URL + close callback.

import {
  createCopyButton,
  createCrestButton,
  el,
  openModal,
  type ModalHandle,
} from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import { isNativePlatform } from '../native/platform.js';
import { shareUrl } from '../native/share.js';

export interface ShareModalOptions {
  readonly url: string;
  readonly onClose?: () => void;
}

export interface ShareModalHandle {
  readonly close: () => void;
}

interface NavigatorShareTarget {
  readonly url: string;
  readonly title?: string;
  readonly text?: string;
}

interface NavigatorWithShare {
  readonly share?: (data: NavigatorShareTarget) => Promise<void>;
  readonly canShare?: (data: NavigatorShareTarget) => boolean;
}

function getShareNavigator(): NavigatorWithShare | null {
  if (typeof navigator === 'undefined') return null;
  return navigator as unknown as NavigatorWithShare;
}

function canUseWebShare(target: NavigatorShareTarget): boolean {
  const nav = getShareNavigator();
  if (nav === null || nav.share === undefined) return false;
  if (nav.canShare === undefined) return true;
  try {
    return nav.canShare(target);
  } catch {
    return false;
  }
}

/**
 * Open the share modal. Returns a handle the caller can use to close it
 * programmatically (e.g. on navigation). The modal also closes via overlay
 * click and the built-in "Close" action.
 */
export function openShareModal(opts: ShareModalOptions): ShareModalHandle {
  const title = t('share.modal.title');
  const target: NavigatorShareTarget = { title, text: title, url: opts.url };

  const urlField = el('input', {
    class: 'sudoku-share-modal__url',
    attrs: { type: 'text', readonly: 'readonly', value: opts.url, 'aria-label': title },
  }) as HTMLInputElement;
  urlField.addEventListener('focus', () => urlField.select());

  const copyBtn = createCopyButton({
    getText: () => opts.url,
    label: t('share.modal.copyButton'),
    copiedLabel: t('share.modal.copied'),
  });

  const actions: HTMLElement[] = [copyBtn];

  if (isNativePlatform() || canUseWebShare(target)) {
    const systemBtn = createCrestButton({
      label: t('share.modal.systemShareButton'),
      small: true,
      onClick: () => {
        void shareUrl({ title, text: title, url: opts.url }).catch(() => {
          // User cancelled or share failed silently; copy path stays available.
        });
      },
    });
    actions.push(systemBtn);
  }

  const actionRow = el('div', { class: 'sudoku-share-modal__actions' }, actions);
  const body = el('div', { class: 'sudoku-share-modal' }, [urlField, actionRow]);

  let handle: ModalHandle | null = null;
  const closeHandler = (): void => {
    handle?.close();
    opts.onClose?.();
  };

  handle = openModal({
    title,
    body,
    actions: [
      { label: t('common.cancel'), primary: true, onClick: closeHandler },
    ],
  });

  // Focus the URL field so screen-readers announce it immediately.
  queueMicrotask(() => urlField.select());

  return { close: closeHandler };
}
